import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaUserShield } from "react-icons/fa";
import authService from "../services/authService";
import { consumeAuthRedirectNotice } from "../utils/authRedirect";
import Footer from "../components/layout/Footer";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const labelClassName =
  "mb-2 flex items-center gap-1 text-sm font-semibold text-slate-200";

const RequiredMark = () => (
  <span className="text-sm font-semibold text-rose-400" aria-hidden="true">
    *
  </span>
);

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [captcha, setCaptcha] = useState(() =>
    Math.floor(1000 + Math.random() * 9000)
  );
  const [captchaInput, setCaptchaInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const notice = consumeAuthRedirectNotice("/admin/login");

    if (notice) {
      setModal({
        isOpen: true,
        type: "error",
        message: notice,
      });
    }
  }, []);

  const refreshCaptcha = () => {
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
    setCaptchaInput("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (captchaInput.trim() !== captcha.toString()) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Invalid captcha. Please try again.",
      });
      refreshCaptcha();
      return;
    }

    setLoading(true);

    try {
      const response = await authService.login(
        formData.email,
        formData.password,
        "admin"
      );

      if (response.status === "success") {
        if (response.data.user.user_type === "admin") {
          navigate("/admin/dashboard", { replace: true });
          return;
        }

        setModal({
          isOpen: true,
          type: "error",
          message: "Access denied. Admin privileges required.",
        });
        await authService.logout();
      } else {
        setModal({
          isOpen: true,
          type: "error",
          message: response.message || "Login failed. Please try again.",
        });
      }
    } catch (error) {
      setModal({
        isOpen: true,
        type: "error",
        message:
          error.message || "An error occurred during login. Please try again.",
      });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const modalClassName =
    modal.type === "error"
      ? "border-rose-400/35 bg-rose-500/10 text-rose-100"
      : "border-emerald-400/35 bg-emerald-500/10 text-emerald-100";

  return (
    <div className="flex min-h-screen flex-col bg-[#111318] text-slate-200">
      <header className="border-b border-slate-700/40 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/20 to-blue-400/10">
              <FaUserShield size={22} className="text-blue-400" />
            </div>

            <div>
              <h1 className="text-[17px] font-semibold text-slate-50">
                Admin Portal
              </h1>
              <div className="text-[11px] font-medium text-slate-500">
                Limbus Intelligence Management
              </div>
            </div>
          </div>

          <Link
            to="/login"
            className="text-sm font-medium text-blue-400 transition hover:text-blue-300"
          >
            Researcher Login
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="flex justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-[560px] rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8"
          >
            <div className="mb-7 text-center">
              <div className="mb-4 flex justify-center">
                <div className="flex h-[72px] w-[72px] items-center justify-center rounded-[20px] border border-blue-500/20 bg-gradient-to-br from-blue-500/20 to-blue-400/10">
                  <FaUserShield size={34} className="text-blue-400" />
                </div>
              </div>

              <h2 className="text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[30px]">
                Admin Login
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Sign in to access the admin dashboard
              </p>
            </div>

            {modal.isOpen && (
              <div
                className={`mb-5 rounded-2xl border px-4 py-3 text-sm leading-6 whitespace-pre-line ${modalClassName}`}
                role="alert"
              >
                {modal.message}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="admin-email" className={labelClassName}>
                Email <RequiredMark />
              </label>
              <input
                id="admin-email"
                type="email"
                name="email"
                placeholder="Admin Email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClassName}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="admin-password" className={labelClassName}>
                Password <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="admin-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`${inputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="admin-captcha" className={labelClassName}>
                Captcha <RequiredMark />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="admin-captcha"
                  type="text"
                  placeholder="Enter captcha"
                  value={captchaInput}
                  onChange={(event) => setCaptchaInput(event.target.value)}
                  required
                  className={inputClassName}
                />
                <div className="flex min-w-[148px] items-center justify-between rounded-xl bg-blue-600 px-4 py-3 font-semibold tracking-[0.22em] text-white sm:justify-center sm:gap-3">
                  <span className="text-sm">{captcha}</span>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    title="Refresh Captcha"
                    className="rounded-full bg-white/10 px-2 py-1 text-sm tracking-normal transition hover:bg-white/20"
                  >
                    ↻
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
            >
              {loading ? "Logging in..." : "Admin Login"}
            </button>

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-blue-400 transition hover:text-blue-300"
              >
                ← Back to Researcher Login
              </Link>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AdminLogin;
